import { useEffect, useId, useState } from 'react';
import { validateSubdomain, type Project } from '@mockd/shared';
import { useProjects } from '../../hooks';
import { getMockApiSubdomainUrl } from '../../config';
import { Alert } from '../common/Alert';
import { Modal } from '../common/Modal';

interface RenameSubdomainModalProps {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
  onRenamed: (project: Project) => void;
}

// Change a project's subdomain, which is its public mock API hostname. The
// server moves the endpoints and rules to the new name, clears request logs,
// and keeps the project's two most recent old names reserved for it.
export function RenameSubdomainModal({ project, isOpen, onClose, onRenamed }: RenameSubdomainModalProps) {
  const inputId = useId();
  const hintId = useId();
  const { renameSubdomain } = useProjects();
  const [subdomain, setSubdomain] = useState(project.subdomain);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSubdomain(project.subdomain);
      setError(null);
    }
  }, [isOpen, project.subdomain]);

  const trimmed = subdomain.trim();
  const unchanged = trimmed === project.subdomain;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (unchanged) return;
    // Same rule the server enforces, so a name it would reject never leaves the form
    const subdomainError = validateSubdomain(trimmed);
    if (subdomainError) {
      setError(subdomainError);
      return;
    }

    setError(null);
    setIsSaving(true);
    try {
      const renamed = await renameSubdomain(project.id, trimmed);
      onRenamed(renamed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change subdomain');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Subdomain" titleClassName="font-bold text-lg mb-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        <Alert variant="warning">
          {getMockApiSubdomainUrl(project.subdomain)} stops working as soon as you save, so update anything
          that calls it. Endpoints, rules and variables move to the new URL; request logs are cleared. Your two
          most recent old subdomains stay reserved for this project, so you can switch back to them; deleting the
          project releases them.
        </Alert>

        <div className="fieldset">
          <label htmlFor={inputId} className="fieldset-legend">
            New subdomain
          </label>
          <input
            type="text"
            id={inputId}
            value={subdomain}
            onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
            className="input w-full"
            aria-describedby={hintId}
            disabled={isSaving}
          />
          <p id={hintId} className="label whitespace-normal text-base-content/80">
            New mock API URL: {getMockApiSubdomainUrl(trimmed || 'your-subdomain')}
          </p>
        </div>

        <div className="modal-action">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={unchanged || !trimmed || isSaving}>
            {isSaving ? 'Saving…' : 'Change subdomain'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
